import {FC, Dispatch, SetStateAction} from "react";
import {Card, CardContent, CardActions, Button, Typography, Chip, CardProps} from "@mui/material";
import {makeStyles} from "tss-react/mui";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import ScheduleIcon from "@mui/icons-material/Schedule";
import CircularProgress from "@mui/material/CircularProgress";
import {NoteDto} from "@/app2/shared/data/gen";
import {NoteEditInput} from "./NoteEditInput";
import ConfirmationDialog from "@/components/common/dialogs/ConfirmationDialog";
import {useSSTranslation} from "@/i18n/useSSTranslation";

const useStyles = makeStyles()((theme) => ({
	noteItemCard: {
		backgroundColor: theme.palette.background.paper,
		border: `1px solid ${theme.palette.secondary.light}`
	},
	cardContent: {
		display: "flex",
		flexDirection: "column",
		height: "auto",
		paddingBottom: theme.spacing(3)
	},
	cardActions: {
		borderTop: `1px solid ${theme.palette.secondary.main}20`,
		paddingTop: theme.spacing(1.5),
		paddingBottom: theme.spacing(1.5),
		"&>:not(:first-of-type)": {
			marginLeft: 0
		}
	},
	actionButton: {
		width: "50%",
		paddingTop: theme.spacing(0.5),
		paddingBottom: theme.spacing(0.5),
		"&:not(:first-of-type)": {
			borderLeft: `1px solid ${theme.palette.secondary.main}20`
		},
		borderRadius: 0
	},
	timestampButton: {
		backgroundColor: theme.palette.background.default,
		border: `1px solid ${theme.palette.secondary.light}`,
		padding: theme.spacing(0.5, 1.2, 0.5, 1.5),
		marginBottom: theme.spacing(1.5)
	},
	timestampIcon: {
		marginRight: 0,
		transform: "translateY(-0.5px)",
		color: theme.palette.secondary.main
	},
	wysiwygRoot: {
		whiteSpace: "pre-wrap",
		"&>p": {
			marginBottom: theme.spacing(0.5)
		}
	},
	noteInput: {},
	savingRow: {
		display: "flex",
		alignItems: "center",
		gap: theme.spacing(1),
		marginTop: theme.spacing(0.5),
		minHeight: theme.spacing(3)
	},
	noteContentWithSaving: {
		display: "flex",
		flexDirection: "column",
		gap: theme.spacing(0.5)
	},
	savingSpinner: {
		width: 14,
		height: 14,
		color: theme.palette.primary.main
	},
	buttonIcon: {},
	linksRoot: {
		color: theme.palette.grey[300],
		marginTop: theme.spacing(1)
	}
}));

const formatTimestamp = (seconds: number): string => {
	if (!seconds || seconds === 0) return "00:00";
	const m = Math.floor(seconds / 60);
	const s = Math.floor(seconds % 60);
	return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
};

export interface NoteItemCardProps extends CardProps {
	note: NoteDto;
	isSourceInvalid: boolean;
	onGoToModuleAction?: (courseSlug: string, moduleSlug: string, moduleType: string, timestamp?: number) => void;
	isEditing: boolean;
	setIsEditing: Dispatch<SetStateAction<boolean>>;
	confirmDeleteModalOpen: boolean;
	setConfirmDeleteModalOpen: (open: boolean) => void;
	handleDeleteNote: () => void;
	handleUpdate: (newValue: string) => void;
	links: JSX.Element[];
	isUpdating?: boolean;
	isDeleting?: boolean;
}

/** Mirrors shapeshift notesLibrary/noteItemCard - presentational only */
export const NoteItemCard: FC<NoteItemCardProps> = ({
	note,
	className,
	onGoToModuleAction,
	isSourceInvalid,
	isEditing,
	confirmDeleteModalOpen,
	handleDeleteNote,
	handleUpdate,
	links,
	setIsEditing,
	setConfirmDeleteModalOpen,
	isUpdating = false,
	isDeleting = false,
	...baseProps
}) => {
	const {classes, cx} = useStyles();
	const {t} = useSSTranslation();
	const {course, module} = note;

	const handleToggleEditing = () => {
		setIsEditing((state) => !state);
	};

	return (
		<Card className={cx(classes.noteItemCard, className)} {...baseProps}>
			<CardContent className={classes.cardContent}>
				{note.videoTimestamp ? (
					<Chip
						label={formatTimestamp(note.videoTimestamp)}
						className={classes.timestampButton}
						disabled={isSourceInvalid}
						onClick={() => {
							if (isSourceInvalid || !course || !module) return;
							onGoToModuleAction?.(course.slug ?? "", module.slug ?? "", module.type, note.videoTimestamp ?? undefined);
						}}
						icon={<ScheduleIcon className={classes.timestampIcon} />}
					/>
				) : null}
				{isEditing ? (
					isUpdating ? (
						<div className={classes.noteContentWithSaving} role="status" aria-live="polite">
							<Typography variant="caption" component="div" className={classes.wysiwygRoot}>
								{note.content.replace(/\n/gi, "\u00A0 \n")}
							</Typography>
							<span className={classes.savingRow}>
								<CircularProgress className={classes.savingSpinner} size={14} />
								<Typography variant="caption" color="text.secondary">
									{t("common_notes_saving", "Saving…")}
								</Typography>
							</span>
						</div>
					) : (
						<NoteEditInput
							className={classes.noteInput}
							initialValue={note.content}
							onPostNote={handleUpdate}
						/>
					)
				) : (
					<Typography variant="caption" component="div" className={classes.wysiwygRoot}>
						{note.content.replace(/\n/gi, "\u00A0 \n")}
					</Typography>
				)}
				{links.length ? (
					<Typography variant="caption" className={classes.linksRoot}>
						{links}
					</Typography>
				) : null}
			</CardContent>
			<CardActions className={classes.cardActions}>
				<Button
					size="large"
					variant="text"
					color="primary"
					className={classes.actionButton}
					onClick={handleToggleEditing}
					startIcon={<EditIcon className={classes.buttonIcon} />}
				>
					{isEditing ? t("common_notes_cancel", "Cancel") : t("common_notes_edit", "Edit")}
				</Button>
				<Button
					size="large"
					variant="text"
					color="primary"
					className={classes.actionButton}
					onClick={() => setConfirmDeleteModalOpen(true)}
					startIcon={<DeleteIcon className={classes.buttonIcon} />}
				>
					{t("common_notes_delete", "Delete")}
				</Button>
			</CardActions>
			<ConfirmationDialog
				open={confirmDeleteModalOpen}
				onClose={() => setConfirmDeleteModalOpen(false)}
				onCancel={() => setConfirmDeleteModalOpen(false)}
				onConfirm={handleDeleteNote}
				title={t("notes_delete_confirm_title", "Are you sure?")}
				content={t("notes_delete_confirm_content", "Once you delete this item, you won't be able to get it back.")}
				testIdPrefix="confirmDeleteNote"
				confirmButtonColor="error"
				confirmButtonTitle={t("common_notes_delete", "Delete")}
				disableConfirmButton={isDeleting}
			/>
		</Card>
	);
};
